
import { supabase } from '@/lib/supabaseClient';

export interface Notification {
    id: string;
    user_id: string;
    type: 'info' | 'warning' | 'success' | 'error' | 'report_received' | 'workout_assigned' | 'workout_completed' | 'client' | 'workout' | 'report' | 'message' | 'payment';
    title: string;
    message?: string; // Alias for body
    body: string;
    data: Record<string, any>;
    is_read: boolean;
    read?: boolean; // Alias for is_read
    action_url?: string;
    actionUrl?: string; // Alias
    clientAvatar?: string;
    time?: Date;
    created_at: string;
}

export const notificationService = {
    /**
     * Get notifications for the current user
     */
    async getNotifications(page = 1, limit = 20) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const from = (page - 1) * limit;
        const to = from + limit - 1;

        const { data, error, count } = await supabase
            .from('notifications')
            .select('*', { count: 'exact' })
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .range(from, to);

        if (error) throw error;
        return { data: data as Notification[], count };
    },

    /**
     * Get unread count
     */
    async getUnreadCount() {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return 0;

        const { count, error } = await supabase
            .from('notifications')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .eq('is_read', false);

        if (error) console.error('Error fetching unread count:', error);
        return count || 0;
    },

    /**
     * Mark a single notification as read
     */
    async markAsRead(notificationId: string) {
        const { error } = await supabase
            .from('notifications')
            .update({ is_read: true })
            .eq('id', notificationId);

        if (error) throw error;
    },

    /**
     * Mark all notifications as read for current user
     */
    async markAllAsRead() {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const { error } = await supabase
            .from('notifications')
            .update({ is_read: true })
            .eq('user_id', user.id)
            .eq('is_read', false); // Optimization: only update unread ones

        if (error) throw error;
    },

    /**
     * Delete a notification
     */
    async deleteNotification(notificationId: string) {
        const { error } = await supabase
            .from('notifications')
            .delete()
            .eq('id', notificationId);

        if (error) throw error;
    },

    /**
     * Delete all notifications for the current user
     */
    async deleteAllNotifications() {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const { error } = await supabase
            .from('notifications')
            .delete()
            .eq('user_id', user.id);

        if (error) throw error;
    },

    /**
     * Create a test notification via backend
     */
    async createTestNotification() {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error('Not authenticated');

        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
        const response = await fetch(`${apiUrl}/notifications/test`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${session.access_token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Failed to create test notification: ${response.statusText}`);
        }
        return response.json();
    }
};
